import { getAccessToken } from './supabase'

// Base API client that works with Cloudflare Workers
const RECORDER_URL = import.meta.env.VITE_RECORDER_URL || 'https://orbglide-recorder.orbglide.workers.dev';
const ANALYZER_URL = import.meta.env.VITE_ANALYZER_URL || 'https://orbglide-analyzer.orbglide.workers.dev';

// In-memory cache for final game stats - persists across refetches
// This prevents the "flash" where stats disappear on refresh
const finalStatsCache = new Map();

// Last correlation ID for debugging - accessible via window.__lastCorrelationId
let lastCorrelationId = null;
if (typeof window !== 'undefined') {
  window.__getDebugInfo = () => ({
    lastCorrelationId,
    analyzerUrl: ANALYZER_URL,
    timestamp: new Date().toISOString(),
  });
}

// Dev mode bypass (for testing only)
const DEV_KEY = import.meta.env.VITE_DEV_KEY || '';

function normalizeTeam(team) {
  if (!team) return null;
  const name = team.name || team.full_name || team.nickname || team.team_name || team.abbreviation || team.code;
  const code = team.code || team.abbreviation || team.abbrev;
  return {
    ...team,
    name: name || team.name,
    code,
  };
}

function normalizeGameTeams(game) {
  if (!game || (Array.isArray(game.teams) && game.teams.length >= 2)) return game;
  const home = normalizeTeam(game.homeTeam || game.home_team || game?.raw?.home_team);
  const away = normalizeTeam(game.awayTeam || game.visitor_team || game?.raw?.visitor_team);
  if (!home && !away) return game;
  
  // Format time from startTime if not already present (NFL games have startTime but no time field)
  let time = game.time;
  if (!time && game.startTime) {
    try {
      const d = new Date(game.startTime);
      if (!Number.isNaN(d.getTime())) {
        time = d.toLocaleTimeString('en-US', { 
          hour: 'numeric', 
          minute: '2-digit', 
          hour12: true,
          timeZone: 'America/New_York'
        });
      }
    } catch { /* ignore */ }
  }
  
  return {
    ...game,
    teams: [home || {}, away || {}],
    time,
  };
}

// Helper to get date in YYYY-MM-DD format using SPORTS CALENDAR
// Sports day ends at 4am ET, not midnight - so late West Coast games stay in "today"
function getSportsDate(offset = 0) {
  // Use Intl.DateTimeFormat to get correct ET date parts
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    hour12: false,
  });
  
  const parts = formatter.formatToParts(new Date()).reduce((acc, p) => {
    if (p.type !== 'literal') acc[p.type] = p.value;
    return acc;
  }, {});
  
  // Build date in ET
  let year = parseInt(parts.year, 10);
  let month = parseInt(parts.month, 10) - 1; // JS months are 0-indexed
  let day = parseInt(parts.day, 10);
  const hour = parseInt(parts.hour, 10);
  
  // If before 4am ET, we're still in "yesterday" sports-wise
  if (hour < 4) {
    day -= 1;
  }
  
  // Create date and apply offset
  const date = new Date(year, month, day);
  date.setDate(date.getDate() + offset);
  
  // Format as YYYY-MM-DD
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function getTomorrowDate() {
  return getSportsDate(1);
}

function getYesterdayDate() {
  return getSportsDate(-1);
}

function getTodayDate() {
  return getSportsDate(0);
}

const apiClient = {
  // Generic request handler (uses Recorder by default)
  async request(endpoint, options = {}, useAnalyzer = false) {
    const baseUrl = useAnalyzer ? ANALYZER_URL : RECORDER_URL;
    const url = `${baseUrl}${endpoint}`;
    
    // Get JWT token if user is logged in
    const token = await getAccessToken();
    
    // Get user's timezone for proper time formatting
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    const headers = {
      'Content-Type': 'application/json',
      // Send JWT if user is logged in (for higher rate limits)
      ...(useAnalyzer && token ? { 'Authorization': `Bearer ${token}` } : {}),
      ...(DEV_KEY && useAnalyzer ? { 'X-Dev-Key': DEV_KEY } : {}),
      // Send timezone for proper time formatting
      ...(useAnalyzer ? { 'X-Timezone': userTimezone } : {}),
      ...(options.headers || {}),
    };
    const response = await fetch(url, {
      ...options,
      headers,
      // Prevent browser caching - this is the proper way
      cache: 'no-store',
    });

    if (!response.ok) {
      // Try to get error details from response body
      let errorMessage = `HTTP error! status: ${response.status}`;
      let errorData = null;
      try {
        const contentType = response.headers.get('content-type') || '';
        if (contentType.includes('application/json')) {
          errorData = await response.json();
          errorMessage = errorData.message || errorData.error || errorMessage;
        }
      } catch {
        // Ignore parse errors
      }
      const error = new Error(errorMessage);
      error.status = response.status;
      error.data = errorData;
      // Surface error to React Query for proper UI handling
      throw error;
    }

    if (response.status === 204) {
      return null;
    }

    // Capture correlation ID for debugging
    const correlationId = response.headers.get('x-correlation-id');
    if (correlationId) {
      lastCorrelationId = correlationId;
    }

    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
      return response.json();
    }
    return response.text();
  },

  // Game-related endpoints
  games: {
    async getGames(date) {
      return apiClient.request(`/api/games?date=${date}`);
    },
    async getGameDetails(gameId) {
      return apiClient.request(`/api/games/${gameId}`);
    },
  },

  // Player-related endpoints
  players: {
    async getPlayerStats(playerId) {
      return apiClient.request(`/api/players/${playerId}/stats`);
    },
  },

  // Chat/Conversation endpoints
  chat: {
    async sendMessage(conversationId, message) {
      return apiClient.sendChatMessage(message, conversationId);
    },
  },

  async sendChatMessage(message, conversationId, context, signal) {
    // Extract gameId from context if available - ensure it's a string
    const rawId = context?.gameId || context?.id || 'default-game';
    const gameId = String(rawId);
    
    // Build sidebar context to pass to AI
    const sidebarContext = {
      playerProps: context?.playerProps || null,
      injuries: context?.injuries || null,
      odds: context?.odds || null,
      game: context?.game || null,
      // User-provided odds from their sportsbook (e.g., Hard Rock)
      userOdds: context?.userOdds || null,
      // Player context from PlayerCardV2 (season avg, recent games, prop lines)
      playerContext: context?.playerContext || null,
      // Injury context from PulseTab injury click
      injuryContext: context?.injuryContext || null,
      // Live event context from PulseTab live feed click
      liveEventContext: context?.liveEventContext || null,
      // Conversation history for follow-up context
      conversationHistory: context?.conversationHistory || null,
    };
    
    const data = await apiClient.request('/api/chat', {
      method: 'POST',
      body: JSON.stringify({ 
        gameId,
        question: message,
        sidebarContext, // Pass sidebar data to AI
      }),
      signal, // AbortController signal for cancellation
    }, true); // Use Analyzer for chat
    
    // Map Analyzer's 'answer' field to frontend's expected 'response' field
    return {
      ...data,
      response: data?.answer || data?.response,
      conversation_id: conversationId, // Preserve for frontend state
    };
  },

  /**
   * Stream chat message via SSE - returns tokens as they arrive
   * @param {string} message - The user's question
   * @param {string} conversationId - Conversation ID (optional)
   * @param {object} context - Game context and sidebar data
   * @param {AbortSignal} signal - AbortController signal for cancellation
   * @param {function} onToken - Callback for each token received
   * @param {function} onDone - Callback when stream completes
   * @param {function} onError - Callback on error
   */
  async sendChatMessageStreaming(message, conversationId, context, signal, onToken, onDone, onError) {
    const rawId = context?.gameId || context?.id || 'none';
    const gameId = String(rawId);
    
    const sidebarContext = {
      playerProps: context?.playerProps || null,
      injuries: context?.injuries || null,
      odds: context?.odds || null,
      game: context?.game || null,
      userOdds: context?.userOdds || null,
      playerContext: context?.playerContext || null,
      injuryContext: context?.injuryContext || null,
      liveEventContext: context?.liveEventContext || null,
      conversationHistory: context?.conversationHistory || null,
    };

    const token = await getAccessToken();
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    try {
      const response = await fetch(`${ANALYZER_URL}/api/chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
          ...(DEV_KEY ? { 'X-Dev-Key': DEV_KEY } : {}),
          'X-Timezone': userTimezone,
        },
        body: JSON.stringify({ gameId, question: message, sidebarContext }),
        signal,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || `HTTP ${response.status}`);
      }

      // Check if we got a JSON error response instead of SSE stream
      const contentType = response.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        const data = await response.json();
        if (data.error) {
          throw new Error(data.answer || data.message || data.error);
        }
        // Non-streaming response - call onToken with full response
        onToken(data.answer || data.response || '');
        onDone({
          agent: data.agent || 'Flash',
          questionType: data.questionType,
          conversation_id: conversationId,
        });
        return;
      }

      // Get metadata from headers
      const agent = response.headers.get('X-Agent') || 'Flash';
      const questionType = response.headers.get('X-Question-Type') || 'general';
      const dataAgeSeconds = parseInt(response.headers.get('X-Data-Age-Seconds') || '0', 10);
      const contextQuality = parseInt(response.headers.get('X-Context-Quality') || '100', 10);

      // Read SSE stream
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let doneReceived = false;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            try {
              const parsed = JSON.parse(data);
              if (parsed.done) {
                doneReceived = true;
                onDone({ agent, questionType, dataAgeSeconds, contextQuality, conversation_id: conversationId });
              } else if (parsed.token) {
                onToken(parsed.token);
              }
            } catch {
              // Ignore parse errors
            }
          }
        }
      }
      if (!doneReceived) {
        onDone({ agent, questionType, dataAgeSeconds, contextQuality, conversation_id: conversationId });
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        // User cancelled - don't call onError
        return;
      }
      onError(err);
    }
  },

  async getLiveGames(date = 'today', league = 'nba') {
    // For NFL, use league-specific endpoint
    if (league === 'nfl') {
      try {
        const gamesData = await apiClient.request(`/api/games/with-odds?league=nfl&_t=${Date.now()}`, {}, true);
        console.log('[NFL] API response:', { gamesDataKeys: gamesData ? Object.keys(gamesData) : null, gamesCount: gamesData?.games?.length });
        let games = gamesData?.games || [];
        
        if (games.length > 0) {
          console.log('[NFL] First game from API:', { id: games[0].id, week: games[0].week, status: games[0].status, keys: Object.keys(games[0]).slice(0, 10) });
        }
        
        // Use ET timezone for sports calendar (sports day ends at 4 AM ET)
        const formatter = new Intl.DateTimeFormat('en-CA', {
          timeZone: 'America/New_York',
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          hour12: false,
        });
        const parts = formatter.formatToParts(new Date()).reduce((acc, p) => {
          if (p.type !== 'literal') acc[p.type] = p.value;
          return acc;
        }, {});
        
        let etDate = new Date(`${parts.year}-${parts.month}-${parts.day}T${parts.hour}:00:00`);
        // If before 4 AM ET, we're still in yesterday's sports day
        if (parseInt(parts.hour) < 4) {
          etDate.setDate(etDate.getDate() - 1);
        }
        const todayStr = etDate.toISOString().split('T')[0]; // YYYY-MM-DD in ET
        
        // Get weeks from games and calculate current week
        console.log('[NFL] Raw games received:', { count: games.length, firstGame: games[0] ? { week: games[0].week, status: games[0].status, keys: Object.keys(games[0]) } : null });
        
        const weeks = [...new Set(games.map(g => g.week || 0))].sort((a, b) => b - a);
        console.log('[NFL] Weeks extracted:', weeks);
        
        // Current week is the one with scheduled games (not final)
        const scheduledWeeks = [...new Set(games.filter(g => !g.status?.toLowerCase().includes('final')).map(g => g.week || 0))].sort((a, b) => a - b);
        const currentWeek = scheduledWeeks[0] || weeks[0] || 0;
        const prevWeek = currentWeek - 1;
        
        console.log('[NFL] Week calculation:', { totalGames: games.length, weeks, scheduledWeeks, currentWeek, prevWeek, date })
        
        // Helper: extract ET date from game (NFL games in UTC, convert to ET)
        const getLocalDateKey = (game) => {
          if (!game?.startTime) return null;
          try {
            const gameDate = new Date(game.startTime);
            if (Number.isNaN(gameDate.getTime())) return null;
            // Convert UTC to ET
            const etFormatter = new Intl.DateTimeFormat('en-CA', {
              timeZone: 'America/New_York',
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
            });
            return etFormatter.format(gameDate);
          } catch {
            return null;
          }
        };
        
        // NFL filtering: Week-based with status fallback
        // "today" = Current week games (scheduled/live, not final)
        // "yesterday" = Previous week games (final games)
        // "next" = Next week games (or upcoming non-final)
        
        const nextWeek = Math.min(22, currentWeek + 1);
        const hasWeekData = games.some(g => g.week != null);
        
        if (date === 'today') {
          if (hasWeekData) {
            // Show ALL games from current week
            const before = games.length;
            console.log(`[NFL] Filtering for week ${currentWeek} (type: ${typeof currentWeek}), games have weeks:`, [...new Set(games.map(g => `${g.week}(${typeof g.week})`))]);
            games = games.filter(g => {
              const match = g.week === currentWeek;
              if (!match && games.length < 50) {
                console.log(`[NFL] Game ${g.id} week ${g.week} (${typeof g.week}) !== ${currentWeek} (${typeof currentWeek})`);
              }
              return match;
            });
            console.log(`[NFL] Filtered 'today' (week ${currentWeek}): ${before} -> ${games.length} games`);
          } else {
            // Fallback: show all games (no week filtering)
            console.log('[NFL] No week data, showing all games');
          }
        } else if (date === 'yesterday') {
          if (hasWeekData && prevWeek > 0) {
            // Show ALL games from previous week
            const before = games.length;
            games = games.filter(g => g.week === prevWeek);
            console.log(`[NFL] Filtered 'yesterday' (week ${prevWeek}): ${before} -> ${games.length} games`);
          } else {
            // Fallback: show all final games
            const before = games.length;
            games = games.filter(g => {
              const status = (g.status || '').toLowerCase();
              return status.includes('final');
            });
            console.log(`[NFL] Filtered 'yesterday' (final games): ${before} -> ${games.length} games`);
          }
        } else if (date === 'tomorrow' || date === 'next') {
          if (hasWeekData) {
            const nextWeekGames = games.filter(g => g.week === nextWeek);
            if (nextWeekGames.length > 0) {
              games = nextWeekGames;
              console.log(`[NFL] Filtered 'next' (week ${nextWeek}): ${nextWeekGames.length} games`);
            } else {
              // Fallback: show all non-final games
              const before = games.length;
              games = games.filter(g => {
                const status = (g.status || '').toLowerCase();
                return !status.includes('final');
              });
              console.log(`[NFL] Filtered 'next' (non-final): ${before} -> ${games.length} games`);
            }
          } else {
            // Fallback: show all non-final games
            const before = games.length;
            games = games.filter(g => {
              const status = (g.status || '').toLowerCase();
              return !status.includes('final');
            });
            console.log(`[NFL] Filtered 'next' (non-final, no week data): ${before} -> ${games.length} games`);
          }
        }
        
        return {
          games: games.map(game => normalizeGameTeams({
            ...game,
            league: 'NFL',
          })),
          league: 'nfl',
          dataStatus: gamesData?.dataStatus || null,
          source: 'nfl',
        };
      } catch (err) {
        console.warn('Failed to fetch NFL games:', err);
        return { games: [], league: 'nfl', source: 'nfl' };
      }
    }
    
    // NBA logic below
    // For tomorrow, just fetch scheduled games (no box scores needed)
    if (date === 'tomorrow') {
      const scheduled = await apiClient.request(`/api/games?date=${getTomorrowDate()}&league=nba`, {}, true);
      return { ...scheduled, source: 'scheduled' };
    }
    
    // For yesterday, fetch games AND their player stats
    if (date === 'yesterday') {
      const dateStr = getYesterdayDate();
      const gamesData = await apiClient.request(`/api/games?date=${dateStr}&league=nba`, {}, true);
      const games = gamesData?.games || [];
      
      // If we have games, fetch their player stats
      if (games.length > 0) {
        const gameIds = games.map(g => g.id).filter(Boolean).join(',');
        try {
          const statsData = await apiClient.request(`/api/boxscores?game_ids=${gameIds}`, {}, true);
          const statsMap = statsData?.stats || {};
          
          // Merge games with player stats
          const mergedGames = games.map(game => {
            const gameStats = statsMap[game.id];
            return {
              ...game,
              players: gameStats ? {
                home: gameStats.home || [],
                away: gameStats.away || [],
              } : null,
            };
          });
          
          return { games: mergedGames, source: 'yesterday' };
        } catch (err) {
          console.warn('Failed to fetch yesterday stats:', err);
        }
      }
      
      return { games, source: 'yesterday' };
    }
    
    // For today: fetch games with odds status + box scores (has player stats)
    let gamesData = null;
    let boxScoresData = null;
    
    try {
      // Use new /games/with-odds endpoint for odds status + fallback
      gamesData = await apiClient.request(`/api/games/with-odds?league=nba&_t=${Date.now()}`, {}, true);
    } catch (err) {
      console.warn('Failed to fetch games with odds, falling back:', err);
      try {
        // Fallback to regular games endpoint
        gamesData = await apiClient.request(`/api/games?league=nba&_t=${Date.now()}`, {}, true);
      } catch (err2) {
        console.warn('Failed to fetch games:', err2);
      }
    }
    
    const games = gamesData?.games || [];
    
    // Fetch LIVE box scores with player stats from /v1/box_scores/live
    // This endpoint has real-time player stats (unlike /v1/stats which lags ~5min)
    let liveBoxScores = [];
    try {
      const boxScoreData = await apiClient.request(`/api/boxscores?_t=${Date.now()}`, {}, true);
      liveBoxScores = boxScoreData?.boxScores || [];
      console.log(`[API] Fetched ${liveBoxScores.length} live box scores with player stats`);
    } catch (err) {
      console.warn('Failed to fetch live box scores:', err);
    }
    
    // Build a map of game ID -> live data from box scores (scores, period, time, players)
    const liveDataMap = {};
    for (const bs of liveBoxScores) {
      const bsId = bs.id || bs.game?.id;
      if (bsId) {
        liveDataMap[bsId] = {
          home_score: bs.home_team_score,
          away_score: bs.visitor_team_score,
          period: bs.period,
          time: bs.time,
          home_players: bs.home_team?.players || [],
          away_players: bs.visitor_team?.players || [],
        };
      }
    }
    
    // Merge: use games as base, but OVERRIDE scores from live box scores
    let mergedGames = games.map(game => {
      // Get live data for this game (keyed by game ID)
      const liveData = liveDataMap[game.id] || liveDataMap[String(game.id)];
      
      // Use live box score data if available (more fresh than /games endpoint)
      const homeScore = liveData?.home_score ?? game.home_team_score;
      const awayScore = liveData?.away_score ?? game.visitor_team_score;
      const period = liveData?.period ?? game.period;
      const time = liveData?.time ?? game.time;
      
      return {
        id: game.id,
        league: 'NBA',
        status: game.status,
        period,
        time,
        home_team: game.home_team,
        visitor_team: game.visitor_team,
        home_team_score: homeScore,
        visitor_team_score: awayScore,
        // Quarter scores from games endpoint (including OT periods)
        home_q1: game.home_q1,
        home_q2: game.home_q2,
        home_q3: game.home_q3,
        home_q4: game.home_q4,
        home_ot1: game.home_ot1,
        home_ot2: game.home_ot2,
        home_ot3: game.home_ot3,
        visitor_q1: game.visitor_q1,
        visitor_q2: game.visitor_q2,
        visitor_q3: game.visitor_q3,
        visitor_q4: game.visitor_q4,
        visitor_ot1: game.visitor_ot1,
        visitor_ot2: game.visitor_ot2,
        visitor_ot3: game.visitor_ot3,
        // Odds from /games/with-odds endpoint
        odds: game.odds || null,
        oddsStatus: game.oddsStatus || null,
        oddsUpdatedAt: game.oddsUpdatedAt || null,
        // Player stats from boxscores endpoint (if available)
        players: liveData ? {
          home: liveData.home_players || [],
          away: liveData.away_players || [],
        } : null,
      };
    });

    // For finished games, use cached stats or fetch from boxscores
    // This ensures finals stay "rock solid" and don't flash empty
    mergedGames = mergedGames.map(game => {
      const status = (game.status || '').toLowerCase();
      const isFinal = status.includes('final');
      
      // Check if game already has players
      const hasPlayers =
        game.players &&
        (Array.isArray(game.players.home) && game.players.home.length > 0) &&
        (Array.isArray(game.players.away) && game.players.away.length > 0);
      
      if (isFinal && hasPlayers) {
        // Cache the stats for this final game
        finalStatsCache.set(game.id, game.players);
        return game;
      }
      
      if (isFinal && !hasPlayers && finalStatsCache.has(game.id)) {
        // Use cached stats - prevents flash on refetch
        return { ...game, players: finalStatsCache.get(game.id) };
      }
      
      return game;
    });

    // Fetch missing final game stats from boxscores API
    const finalsNeedingStats = mergedGames
      .filter(g => {
        const status = (g.status || '').toLowerCase();
        const hasPlayers =
          g.players &&
          (Array.isArray(g.players.home) && g.players.home.length > 0) &&
          (Array.isArray(g.players.away) && g.players.away.length > 0);
        return status.includes('final') && !hasPlayers;
      })
      .map(g => g.id)
      .filter(Boolean);

    if (finalsNeedingStats.length > 0) {
      try {
        const statsResponse = await apiClient.request(
          `/api/boxscores?game_ids=${finalsNeedingStats.join(',')}`,
          {},
          true
        );
        const statsMap = statsResponse?.stats || {};
        mergedGames = mergedGames.map(game => {
          if (!finalsNeedingStats.includes(game.id)) return game;
          const gameStats = statsMap[game.id];
          if (!gameStats) return game;
          const players = {
            home: gameStats.home || [],
            away: gameStats.away || [],
          };
          // Cache for future refetches
          finalStatsCache.set(game.id, players);
          return { ...game, players };
        });
      } catch (err) {
        console.warn('Failed to fetch final game stats:', err);
      }
    }
    
    return { games: mergedGames.map(normalizeGameTeams), source: 'merged' };
  },

  async getGameStats(gameId, league = 'nba') {
    // Use unified game-stats endpoint for both NBA and NFL
    try {
      const response = await apiClient.request(`/api/game-stats/${gameId}?league=${league}`, {}, true);
      if (response?.home && response?.away) {
        return {
          home: response.home || [],
          away: response.away || [],
          game: response.game,
          source: response.source,
        };
      }
    } catch (err) {
      console.warn(`[API] game-stats failed for ${gameId}, falling back to boxscores:`, err);
    }
    
    // Fallback to legacy boxscores endpoint (NBA only)
    const response = await apiClient.request(`/api/boxscores?game_ids=${gameId}`, {}, true);
    const gameStats = response?.stats?.[gameId] || response?.stats?.[String(gameId)];
    if (gameStats) {
      return {
        home: gameStats.home || [],
        away: gameStats.away || [],
        home_team_id: gameStats.home_team_id,
        visitor_team_id: gameStats.visitor_team_id,
      };
    }
    return null;
  },

  async getOdds() {
    return apiClient.request('/api/odds', {}, true);
  },

  async getGameOdds(gameId) {
    return apiClient.request(`/api/odds/${gameId}`, {}, true);
  },

  async getPlayerStats(playerId, league = 'nba') {
    return apiClient.request(`/api/player/${playerId}?league=${league}`, {}, true);
  },

  async getPlayerStatsByName(playerName, teamAbbr = null) {
    const teamParam = teamAbbr ? `?team=${teamAbbr}` : '';
    return apiClient.request(`/api/player/search/${encodeURIComponent(playerName)}${teamParam}`, {}, true);
  },

  async getGamePreview(gameId, league = 'nba') {
    return apiClient.request(`/api/game/${gameId}/preview?league=${league}`, {}, true);
  },

  // Pulse feed - injuries, news, line movements
  async getPulse(league = 'nba') {
    return apiClient.request(`/api/pulse?league=${league}`, {}, true);
  },

  // NFL game context (prebaked with source/missing/freshness)
  async getNFLGameContext(gameId) {
    return apiClient.request(`/api/nfl/game-context/${gameId}`, {}, true);
  },

  // NFL fantasy rankings
  async getNFLFantasyRankings(position = 'all', limit = 20) {
    return apiClient.request(`/api/nfl/fantasy-rankings?position=${position}&limit=${limit}`, {}, true);
  },

  // NFL team rollup
  async getNFLTeamRollup(abbr) {
    return apiClient.request(`/api/nfl/team/${abbr}`, {}, true);
  },

  // NFL player rollup (fantasy)
  async getNFLPlayerRollup(playerId) {
    return apiClient.request(`/api/nfl/player-rollup/${playerId}`, {}, true);
  },

  // Archive endpoints - for games older than yesterday
  async getArchivedGame(gameId) {
    return apiClient.request(`/api/archive/${gameId}`, {}, true);
  },

  async getArchivedGames(date) {
    // date should be YYYY-MM-DD format
    return apiClient.request(`/api/archive/date/${date}`, {}, true);
  },

  async refreshFeed() {
    return apiClient.request('/api/feed/refresh', {
      method: 'POST',
    });
  },

  // Conversation persistence (logged-in users only)
  async getConversations() {
    return apiClient.request('/api/conversations', {}, true);
  },

  async saveConversations(conversations) {
    return apiClient.request('/api/conversations', {
      method: 'POST',
      body: JSON.stringify({ conversations }),
    }, true);
  },

  async deleteConversation(conversationId) {
    return apiClient.request(`/api/conversations/${conversationId}`, {
      method: 'DELETE',
    }, true);
  },

  async getSuggestions(league = 'nba', context = 'home', gameId = null) {
    const params = new URLSearchParams({ league, context });
    if (gameId) params.append('gameId', gameId.toString());
    return apiClient.request(`/api/suggestions?${params}`, {}, true);
  },
};

export default apiClient;
